import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useLocation, useHistory } from 'react-router-dom';
import { brand } from '../../config/brand';
import SocialAvatar from '../../components/SocialAvatar';
import {
  Link2,
  Trash2,
  Plus,
  Edit3,
  CheckCircle2,
  AlertCircle,
  Activity,
  Loader2,
  RefreshCw,
  ArrowRight,
  Share2,
  MessageSquare,
  Globe,
  GripVertical
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getPlatformIcon, getPlatformColor } from '../../utils/platformIcons';
import { useProjects } from '../../contexts/ProjectContext';
import { usePostModal } from '../../contexts/PostModalContext';
import { useGlobalState } from '../../contexts/GlobalStateContext';
import { cn } from "../../lib/utils";

// shadcn UI
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Card,
  CardContent,
  CardFooter,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Label } from "../../components/ui/label";
import { Separator } from "../../components/ui/separator";
import { toast } from "sonner";

import { API_BASE_URL } from '../../config';

import SocialConnectionModal from '../../components/SocialConnectionModal';

const PLATFORM_OPTIONS = [
  { id: 'twitter', name: 'X (Twitter)' },
  { id: 'tiktok', name: 'TikTok' },
  { id: 'youtube', name: 'YouTube' },
  { id: 'instagram', name: 'Instagram' },
  { id: 'threads', name: 'Threads' },
  { id: 'bluesky', name: 'Bluesky' },
  { id: 'tumblr', name: 'Tumblr' },
  { id: 'discord', name: 'Discord' },
  { id: 'telegram', name: 'Telegram' },
];

function SortableAccountRow({ account, children }: { account: any; children: React.ReactNode }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: account.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.7 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} className="relative">
      <div className="absolute left-0 top-0 bottom-0 flex items-center pl-2 z-10">
        <button
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 rounded-md text-muted-foreground/40 hover:text-muted-foreground hover:bg-muted/50 transition-colors"
          tabIndex={-1}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </div>
      {children}
    </div>
  );
}

export default function SocialConnections(): JSX.Element {
  const location = useLocation();
  const history = useHistory();
  const { projects, activeProject, refreshProjects } = useProjects();
  const { openPostModal } = usePostModal();
  const { updateLocalStorage, localStorage: globalState } = useGlobalState();
  const token = localStorage.getItem('token');
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [openAdd, setOpenAdd] = useState(false);
  const [initialPlatform, setInitialPlatform] = useState('twitter');
  const [openEdit, setOpenEdit] = useState(false);
  const [editingAccount, setEditingAccount] = useState<any>(null);
  const [editFormData, setEditFormData] = useState({
    language: 'en',
    gameIds: [] as string[],
    botName: ''
  });
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [isReturning, setIsReturning] = useState(false);
  
  // Discord Setup State
  const [openDiscordSetup, setOpenDiscordSetup] = useState(false);
  const [discordGuildId, setDiscordGuildId] = useState('');
  const [discordChannels, setDiscordChannels] = useState<any[]>([]);
  const [selectedDiscordChannels, setSelectedDiscordChannels] = useState<any[]>([]);
  const [botNickname, setBotNickname] = useState('Invira');
  const [isFinalizingDiscord, setIsFinalizingDiscord] = useState(false);

  // Refs to avoid dependency issues in useEffect
  const processedSearchRef = useRef<string | null>(null);
  const globalStateRef = useRef(globalState);
  const openPostModalRef = useRef(openPostModal);
  
  // Keep refs updated
  useEffect(() => {
    globalStateRef.current = globalState;
    openPostModalRef.current = openPostModal;
  }, [globalState, openPostModal]);

  // Check for auto-add and return parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('status');
    const returnToPost = params.get('returnToPost') === 'true';

    if (status === 'success' && returnToPost) {
      setIsReturning(true);
    }
    
    const addPlatform = params.get('add');
    if (addPlatform && !openAdd) {
      setInitialPlatform(addPlatform);
      setOpenAdd(true);
    }
  }, [location.search, openAdd]);

  // Initialize selected projects with active project when opening dialog
  useEffect(() => {
    if (openAdd && activeProject) {
      setSelectedProjectIds([activeProject.id]);
    } else if (!openAdd) {
      setSelectedProjectIds([]);
    }
  }, [openAdd, activeProject]);

  const fetchAccounts = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/social-accounts/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccounts(response.data);
    } catch (err: any) {
      console.error('Error fetching social connections:', err);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [token]);

  const fetchDiscordChannels = useCallback(async (guildId: string, currentAccounts?: any[]) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/discord/guild/${guildId}/channels`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiscordChannels(response.data);
      
      // If we're managing existing channels, pre-select them
      if (currentAccounts) {
        const connectedForGuild = currentAccounts
          .filter(a => a.platform === 'discord' && a.refreshToken === guildId)
          .map(a => ({
            id: a.externalId,
            name: a.handle.replace('#', ''),
            guildName: a.name.replace('Server: ', ''),
            guildIcon: a.avatarUrl ? a.avatarUrl.split('/')[5].split('.')[0] : null
          }));
        setSelectedDiscordChannels(connectedForGuild);
      }
    } catch (err) {
      toast.error('Failed to fetch Discord channels');
    }
  }, [token]);

  const handleFinalizeDiscord = async () => {
    if (selectedDiscordChannels.length === 0) {
      toast.error('Please select at least one channel');
      return;
    }

    try {
      setIsFinalizingDiscord(true);
      const params = new URLSearchParams(location.search);
      const gameIds = params.get('gameIds')?.split(',') || [];
      const returnToPost = params.get('returnToPost') === 'true';

      await axios.post(`${API_BASE_URL}/auth/discord/finalize`, {
        guildId: discordGuildId,
        channels: selectedDiscordChannels,
        gameIds,
        botName: botNickname
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Discord channels connected!');
      setOpenDiscordSetup(false);
      fetchAccounts();
      refreshProjects();

      if (returnToPost) {
        history.push('/?returnToPost=true');
      } else {
        history.replace(location.pathname);
      }
    } catch (err) {
      toast.error('Failed to save Discord channels');
    } finally {
      setIsFinalizingDiscord(false);
    }
  };

  const handleCheckHealth = async (accountId: string) => {
    try {
      toast.promise(
        axios.post(`${API_BASE_URL}/social-accounts/${accountId}/check-health`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        {
          loading: 'Checking connection health...',
          success: (res) => {
            fetchAccounts(false);
            return res.data.isValid ? 'Connection is alive!' : 'Connection has issues.';
          },
          error: 'Error checking connection.'
        }
      );
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    // Prevent processing the same OAuth callback multiple times
    if (processedSearchRef.current === location.search && location.search !== '') {
      return;
    }
    
    const params = new URLSearchParams(location.search);
    const status = params.get('status');
    const platform = params.get('platform');
    const accountId = params.get('accountId');
    const returnToPost = params.get('returnToPost') === 'true';
    const alreadyConnected = params.get('alreadyConnected') === 'true';

    if (status === 'discord_setup') {
      const guildId = params.get('guildId');
      if (guildId) {
        processedSearchRef.current = location.search;
        setDiscordGuildId(guildId);
        setOpenDiscordSetup(true);
        fetchDiscordChannels(guildId);
      }
    } else if (status === 'success') {
      processedSearchRef.current = location.search;
      
      if (alreadyConnected) {
        toast.info(`Account already linked to this project!`, {
          description: "Existing credentials were refreshed.",
          duration: 5000
        });
      } else {
        toast.success(`Connected to ${platform}!`);
      }

      // Auto-select the newly created account
      if (accountId) {
        const currentSelected = globalStateRef.current.selectedPlatforms || [];
        if (!currentSelected.includes(accountId)) {
          updateLocalStorage({ selectedPlatforms: [...currentSelected, accountId] });
        }
      }

      // Start the return process immediately if needed
      if (returnToPost) {
        // Reduced delay and ensure we navigate back
        setTimeout(() => {
          console.log('[SocialConnections] Redirecting to home...');
          setIsReturning(false); // Clear before redirecting
          history.push('/'); 
          
          // Small extra delay to let route change settle before triggering modal
          setTimeout(() => {
            console.log('[SocialConnections] Opening restored post modal...');
            openPostModalRef.current();
          }, 100);
        }, 800);
      } else {
        setTimeout(() => {
          history.replace(location.pathname);
          setIsReturning(false);
        }, 2000);
      }

      refreshProjects();
    } else if (status === 'error') {
      processedSearchRef.current = location.search;
      const message = params.get('message');
      toast.error(`Failed to connect to ${platform}`, {
        description: message || "Please try again or check your account settings.",
        duration: 8000
      });
    }

    fetchAccounts();
  }, [location.search, location.pathname, fetchAccounts, fetchDiscordChannels, history, refreshProjects, updateLocalStorage]);

  const handleEditAccount = (account: any) => {
    setEditingAccount(account);
    setEditFormData({
      language: account.language || 'en',
      gameIds: account.games?.map((g: any) => g.gameId) || [],
      botName: account.platform === 'discord' ? 'Invira' : '' // Default to Invira if not stored
    });
    setSelectedProjectIds(account.games?.map((g: any) => g.gameId) || []);
    setOpenEdit(true);
  };

  const handleSaveAccount = async () => {
    try {
      await axios.put(`${API_BASE_URL}/social-accounts/${editingAccount.id}`, {
        language: editFormData.language,
        gameIds: selectedProjectIds,
        botName: editingAccount.platform === 'discord' ? editFormData.botName : undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAccounts();
      refreshProjects();
      setOpenEdit(false);
      toast.success('Account updated successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error updating account.');
    }
  };

  const handleDeleteAccount = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this connection? It will be unlinked from all projects.')) return;
    try {
      await axios.delete(`${API_BASE_URL}/social-accounts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAccounts();
      refreshProjects();
      toast.success('Account disconnected');
    } catch (err: any) {
      toast.error('Error deleting connection.');
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = accounts.findIndex(a => a.id === active.id);
    const newIndex = accounts.findIndex(a => a.id === over.id);
    const reordered = arrayMove(accounts, oldIndex, newIndex);
    setAccounts(reordered);

    try {
      await axios.put(`${API_BASE_URL}/social-accounts/reorder`, {
        orderedIds: reordered.map(a => a.id)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      refreshProjects();
    } catch (err) {
      // Revert on error
      fetchAccounts(false);
      toast.error('Failed to save order');
    }
  };

  const getIcon = (platform: string) => getPlatformIcon(platform, 'h-4 w-4');

  if (isReturning) {
    return (
      <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-background text-foreground animate-in fade-in duration-500">
        <div className="relative mb-8">
          <div className="absolute -inset-4 bg-primary/20 rounded-full blur-2xl animate-pulse" />
          <Loader2 className="h-16 w-16 animate-spin text-primary relative z-10" />
        </div>
        <h2 className="text-2xl font-black tracking-tight mb-2">Account Connected!</h2>
        <p className="text-muted-foreground font-medium animate-pulse">Restoring your post draft...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-10 px-4 max-w-4xl space-y-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="bg-primary/10 p-3 rounded-2xl border border-primary/20 shadow-lg shadow-primary/5">
            <Link2 className="h-8 w-8 text-primary" />
          </div>
          <div>
            <h1 className="text-4xl font-black text-foreground tracking-tight italic uppercase">Social Connections</h1>
            <p className="text-muted-foreground font-medium">Authorized accounts for your content publishing.</p>
          </div>
        </div>
        <Button 
          onClick={() => setOpenAdd(true)}
          className="bg-primary hover:bg-primary/90 text-white font-bold h-12 px-6 rounded-xl shadow-lg shadow-primary/20"
        >
          <Plus className="mr-2 h-5 w-5" strokeWidth={3} />
          Connect Social Account
        </Button>
      </div>

      <Card className="bg-card border-border shadow-2xl overflow-hidden min-h-[400px]">
        <CardContent className="p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 space-y-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <p className="text-muted-foreground font-medium">Fetching your connections...</p>
            </div>
          ) : (
            <>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={accounts.map(a => a.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-4">
              {accounts.map((account) => (
                <SortableAccountRow key={account.id} account={account}>
                <div
                  className="group flex flex-col md:flex-row md:items-center justify-between p-5 pl-10 bg-background rounded-2xl border border-border hover:border-primary/20 hover:bg-background transition-all duration-300"
                >
                  <div className="flex items-center gap-4">
                    <div 
                      className={cn(
                        "h-12 w-12 rounded-full border flex items-center justify-center bg-muted group-hover:scale-110 transition-transform shadow-inner overflow-hidden relative",
                        !account.isValid && "border-red-500/50"
                      )}
                      style={{ 
                        borderColor: account.isValid ? getPlatformColor(account.platform) : undefined, 
                        color: getPlatformColor(account.platform) 
                      }}
                    >
                      <SocialAvatar 
                        accountId={account.id}
                        platform={account.platform}
                        name={account.name}
                        avatarUrl={account.avatarUrl}
                        imgClassName={cn(!account.isValid && "grayscale opacity-50")}
                        iconClassName="h-6 w-6"
                      />
                      
                      {!account.isValid && (
                        <div className="absolute inset-0 flex items-center justify-center bg-red-500/20 backdrop-blur-[1px] z-10">
                          <AlertCircle className="h-6 w-6 text-red-500 drop-shadow-md" />
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={cn("text-lg font-bold text-foreground", !account.isValid && "text-muted-foreground")}>{account.name}</span>
                        {!account.isValid && (
                          <div className="flex items-center gap-2">
                            <Badge variant="destructive" className="bg-red-500/10 text-red-500 border-red-500/20 text-[9px] px-1.5 py-0 font-black animate-pulse">
                              EXPIRED / REVOKED
                            </Badge>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-6 px-2 py-0 text-[10px] font-black uppercase tracking-widest bg-primary/10 border-primary/30 text-primary hover:bg-primary hover:text-foreground transition-all"
                              onClick={() => {
                                setInitialPlatform(account.platform);
                                setOpenAdd(true);
                              }}
                            >
                              <RefreshCw className="mr-1 h-3 w-3" />
                              Reconnect
                            </Button>
                          </div>
                        )}
                        <div className="flex gap-1">
                          {account.games?.map((g: any) => {
                            const project = projects.find(p => p.id === g.gameId);
                            return project ? (
                              <Badge key={g.gameId} variant="outline" className="bg-card/50 text-[10px] text-muted-foreground border-border px-1.5 py-0">
                                {project.name}
                              </Badge>
                            ) : null;
                          })}
                        </div>
                      </div>
                      <div className="text-muted-foreground text-sm font-medium uppercase tracking-widest mt-0.5 flex items-center gap-2">
                        <div className="flex items-center gap-1.5 bg-card/30 px-2 py-0.5 rounded-md border border-border/50">
                          <span style={{ color: getPlatformColor(account.platform) }}>
                            {getPlatformIcon(account.platform, 'h-3.5 w-3.5')}
                          </span>
                          <span className="text-[10px]">{account.platform}</span>
                        </div>
                        {account.language && (
                          <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 text-[9px] px-1 py-0 font-black flex items-center gap-1">
                            <span>
                              {account.language === 'en' ? '🇺🇸' : 
                               account.language === 'es' ? '🇪🇸' : 
                               account.language === 'fr' ? '🇫🇷' : 
                               account.language === 'de' ? '🇩🇪' : 
                               account.language === 'pt' ? '🇵🇹' : 
                               account.language === 'it' ? '🇮🇹' : 
                               account.language === 'jp' ? '🇯🇵' : 
                               account.language === 'cn' ? '🇨🇳' : '🌐'}
                            </span>
                            {account.language.toUpperCase()}
                          </Badge>
                        )}
                        {account.handle && (
                          <>
                            <Separator orientation="vertical" className="h-3 bg-card" />
                            <span className="text-primary/60 lowercase tracking-normal font-mono">{account.handle}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 mt-4 md:mt-0 ml-auto md:ml-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleCheckHealth(account.id)}
                      className={cn(
                        "rounded-full h-9 w-9",
                        account.isValid ? "text-green-500/50 hover:text-green-500 hover:bg-green-500/10" : "text-red-500 hover:bg-red-500/10"
                      )}
                      title="Check connection health"
                    >
                      <Activity className="h-4 w-4" />
                    </Button>
                    <Separator orientation="vertical" className="h-6 bg-card hidden md:block" />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditAccount(account)}
                      className="text-primary hover:bg-primary/10 hover:text-primary/80 rounded-lg h-9"
                    >
                      <Edit3 className="mr-2 h-4 w-4" />
                      Edit
                    </Button>
                    <Separator orientation="vertical" className="h-6 bg-card hidden md:block" />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteAccount(account.id)}
                      className="text-red-500/50 hover:text-red-500 hover:bg-red-500/10 rounded-full h-9 w-9"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                </SortableAccountRow>
              ))}
            </div>
              </SortableContext>
            </DndContext>

              {accounts.length === 0 && (
                <div className="flex flex-col items-center justify-center py-20 text-center space-y-6">
                  <div className="bg-muted p-8 rounded-full border border-border shadow-inner">
                    <Share2 className="h-16 w-16 text-muted-foreground" strokeWidth={1} />
                  </div>
                  <div className="space-y-2 max-w-xs">
                    <h3 className="text-xl font-bold text-foreground">No connections yet</h3>
                    <p className="text-muted-foreground text-sm">Link your first social media account to start scheduling content across platforms.</p>
                  </div>
                  <Button
                    onClick={() => setOpenAdd(true)}
                    variant="outline"
                    className="border-primary/30 text-primary hover:bg-primary hover:text-foreground"
                  >
                    Connect Social Account
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
        <CardFooter className="bg-muted/50 border-t border-border px-6 py-4">
          <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-[0.2em] flex items-center gap-2">
            <CheckCircle2 className="h-3 w-3 text-primary" />
            Official OAuth integration enabled for X (Twitter) & TikTok
          </p>
        </CardFooter>
      </Card>

      <SocialConnectionModal 
        open={openAdd} 
        onOpenChange={setOpenAdd} 
        initialPlatform={initialPlatform}
        onSuccess={fetchAccounts}
      />

      {/* Discord Setup Dialog */}
      <Dialog open={openDiscordSetup} onOpenChange={setOpenDiscordSetup}>
        <DialogContent className="bg-card border-border text-foreground sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
              <MessageSquare className="h-6 w-6 text-[#5865F2]" />
              Setup Discord Channels
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Select the channels where you want to publish your posts.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-6 space-y-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground uppercase text-[10px] font-black tracking-widest">
                Bot Nickname (In this server)
              </Label>
              <Input 
                value={botNickname}
                onChange={(e) => setBotNickname(e.target.value)}
                placeholder="e.g. Invira"
                className="bg-background border-border h-11"
              />
              <p className="text-[10px] text-muted-foreground italic">
                This is how the bot will appear in the server members list and messages.
              </p>
            </div>

            <Separator className="bg-card" />

            <Label className="text-muted-foreground uppercase text-[10px] font-black tracking-widest">
              Available Channels
            </Label>
            
            <ScrollArea className="h-[250px] rounded-xl border border-border bg-background p-2">
              {discordChannels.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full py-10 opacity-50">
                  <Loader2 className="h-8 w-8 animate-spin mb-2" />
                  <p className="text-xs">Loading channels...</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {discordChannels.map((channel) => (
                    <div 
                      key={channel.id} 
                      className="flex items-center space-x-3 p-3 rounded-lg hover:bg-card cursor-pointer transition-colors group"
                      onClick={() => {
                        const exists = selectedDiscordChannels.find(c => c.id === channel.id);
                        setSelectedDiscordChannels(exists 
                          ? selectedDiscordChannels.filter(c => c.id !== channel.id) 
                          : [...selectedDiscordChannels, { 
                              id: channel.id, 
                              name: channel.name,
                              guildName: channel.guildName,
                              guildIcon: channel.guildIcon
                            }]
                        );
                      }}
                    >
                      <Checkbox 
                        id={`discord-ch-${channel.id}`} 
                        checked={!!selectedDiscordChannels.find(c => c.id === channel.id)}
                        className="border-border data-[state=checked]:bg-[#5865F2] data-[state=checked]:border-[#5865F2]"
                      />
                      <div className="flex-1">
                        <Label 
                          htmlFor={`discord-ch-${channel.id}`} 
                          className="text-foreground font-bold text-sm cursor-pointer block"
                        >
                          #{channel.name}
                        </Label>
                        <p className="text-[10px] text-muted-foreground font-medium">
                          {channel.type === 5 ? 'Announcement Channel' : 'Text Channel'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
            
            <p className="text-[10px] text-muted-foreground italic">
              Note: You can connect multiple channels from the same server. Each channel will appear as a separate connection in {brand.name}.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setOpenDiscordSetup(false)} className="text-muted-foreground hover:text-foreground">Cancel</Button>
            <Button 
              onClick={handleFinalizeDiscord} 
              disabled={selectedDiscordChannels.length === 0 || isFinalizingDiscord}
              className="bg-[#5865F2] hover:bg-[#4752c4] text-white font-bold h-11 px-6 rounded-xl shadow-lg shadow-[#5865F2]/10 active:scale-[0.98]"
            >
              {isFinalizingDiscord ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Finish Connection
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Connection Dialog */}
      <Dialog open={openEdit} onOpenChange={setOpenEdit}>
        <DialogContent className="bg-card border-border text-foreground sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
              <Edit3 className="h-6 w-6 text-primary" />
              Edit Connection
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Update settings for <span className="text-primary font-bold">{editingAccount?.name}</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-6 space-y-6">
            {editingAccount?.platform === 'discord' && (
              <>
                <div className="space-y-3">
                  <Label className="text-muted-foreground uppercase text-[10px] font-black tracking-widest flex items-center gap-2">
                    <MessageSquare className="h-3 w-3" />
                    Bot Nickname (Server-wide)
                  </Label>
                  <Input 
                    value={editFormData.botName}
                    onChange={(e) => setEditFormData({ ...editFormData, botName: e.target.value })}
                    placeholder="Invira"
                    className="bg-background border-border h-11"
                  />
                  <p className="text-[10px] text-muted-foreground italic">
                    Changing this will update the bot's name in the entire Discord server.
                  </p>
                </div>

                <div className="space-y-3">
                  <Label className="text-muted-foreground uppercase text-[10px] font-black tracking-widest flex items-center gap-2">
                    <Plus className="h-3 w-3" />
                    Channels
                  </Label>
                  <Button 
                    variant="outline" 
                    className="w-full bg-[#5865F2]/10 border-[#5865F2]/30 text-[#5865F2] hover:bg-[#5865F2] hover:text-foreground transition-all h-11"
                    onClick={() => {
                      setDiscordGuildId(editingAccount.refreshToken); // We store guildId in refreshToken
                      setOpenDiscordSetup(true);
                      fetchDiscordChannels(editingAccount.refreshToken, accounts);
                      setOpenEdit(false);
                    }}
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Manage Server Channels
                  </Button>
                  <p className="text-[10px] text-muted-foreground italic">
                    Click to add or remove other channels from this server.
                  </p>
                </div>

                <Separator className="bg-card" />
              </>
            )}

            <div className="space-y-3">
              <Label className="text-muted-foreground uppercase text-[10px] font-black tracking-widest flex items-center gap-2">
                <Globe className="h-3 w-3" />
                Primary Language
              </Label>
              <Select 
                value={editFormData.language} 
                onValueChange={(v) => setEditFormData({ ...editFormData, language: v })}
              >
                <SelectTrigger className="bg-background border-border h-11">
                  <SelectValue placeholder="Select language..." />
                </SelectTrigger>
                <SelectContent className="bg-card border-border text-foreground">
                  <SelectItem value="en">English (EN)</SelectItem>
                  <SelectItem value="es">Spanish (ES)</SelectItem>
                  <SelectItem value="fr">French (FR)</SelectItem>
                  <SelectItem value="pt">Portuguese (PT)</SelectItem>
                  <SelectItem value="de">German (DE)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground italic">
                This language will be used to prioritize trends and generate content for this account.
              </p>
            </div>

            <Separator className="bg-card" />

            <div className="space-y-3">
              <Label className="text-muted-foreground uppercase text-[10px] font-black tracking-widest flex items-center gap-2">
                <Share2 className="h-3 w-3" />
                Assigned Projects
              </Label>
              <ScrollArea className="h-[180px] rounded-xl border border-border bg-background p-4">
                <div className="space-y-3">
                  {projects.map((project) => (
                    <div key={project.id} className="flex items-center space-x-3 group cursor-pointer" onClick={() => {
                      const exists = selectedProjectIds.includes(project.id);
                      setSelectedProjectIds(exists 
                        ? selectedProjectIds.filter(id => id !== project.id) 
                        : [...selectedProjectIds, project.id]
                      );
                    }}>
                      <Checkbox 
                        id={`edit-project-${project.id}`} 
                        checked={selectedProjectIds.includes(project.id)}
                        className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                      />
                      <Label htmlFor={`edit-project-${project.id}`} className="text-foreground group-hover:text-foreground transition-colors cursor-pointer flex-1 py-1">
                        {project.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setOpenEdit(false)} className="text-muted-foreground hover:text-foreground">Cancel</Button>
            <Button 
              onClick={handleSaveAccount} 
              className="bg-primary hover:bg-primary/90 text-white font-bold h-11 px-6 rounded-xl shadow-lg shadow-primary/10 active:scale-[0.98]"
            >
              Save Settings
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
